import { CHAT_STEP_LIMITS, CHAT_STEP_STATES } from '../chat-step-limits'
import { RUN_STATUSES } from '../run-status'
import { type BuiltInTool, OPEN_OBJECT_SCHEMA } from './types'

/**
 * Tools the CEO chat offers a member and a run never answers. A task that lists one is refused when it is saved.
 * Chat also offers `os.directory`, which a run answers too, so it is registered with the company-state tools.
 * `os.list_runs` is the MCP server's, over `GET /v1/runs`: it sits here, beside `os.get_run`, and chat does not
 * offer it. A run may call it too, answered by the OS MCP server over the run's own enterprise, so a scheduling task
 * can see how deep a queue is. `os.kpi_board` (#898) is the same kind: an MCP/API read, answered by an HTTP route rather than by the
 * run harness, so no run dispatcher claims it. So is `os.list_enterprise_kpis` (#1060), over `GET /v1/kpis`, and so
 * are the Waiting list's two actions, `os.finish_waiting` and `os.restart_waiting`, over `POST /v1/waiting/*`, and
 * Blockers' Clear and Cancel, `os.clear_blocker`, over `POST /v1/runs/:runId/clear-stopped` (#954).
 *
 * An entry whose callers include `mcp` is what the MCP server serves, word for word (#1011): its description and
 * schema are what `tools/list` shows, its `api` is the call `tools/call` makes, and the schema is strict, so an
 * argument the tool does not take is refused by name before any call.
 */
export const OS_CHAT_TOOLS: readonly BuiltInTool[] = [
    {
        name: 'os.list_tasks',
        family: 'os.chat',
        summary: 'The tasks of the roles the chatting member holds, plus OS-owned tasks.',
        description:
            'List every task of a role you hold, whatever team you sit on, plus OS-owned tasks. OS tasks are not a person.',
        inputSchema: OPEN_OBJECT_SCHEMA,
        callers: ['chat', 'api'],
        effect: 'read',
        retry: 'safe',
        errors: [],
        api: { method: 'GET', path: '/v1/tasks/held-by/:memberId', scope: 'tasks:read' },
        public: true,
        chatPrompt: 'os.list_tasks lists the tasks of the roles you hold, plus OS tasks.',
    },
    {
        name: 'os.start_task',
        family: 'os.chat',
        summary: "Queue a run of a task of a role you hold, an OS task, or a colleague's task.",
        description:
            'Start a run of a task of a role you hold, an OS task, or a colleague’s task. Pass taskId or slug, and input matching the task’s fields; a field you leave out takes the task’s default. The run goes to a free member holding the task’s role, who may not be you; when every holder is busy it queues as pending. This only queues the run: check it with os.get_run before reporting on it.',
        inputSchema: {
            type: 'object',
            properties: {
                taskId: { type: 'string', description: 'The task id; pass exactly one of taskId or slug.' },
                slug: { type: 'string', description: 'The task slug; pass exactly one of taskId or slug.' },
                input: { type: 'object', description: 'Input matching the task fields.' },
                idempotencyKey: {
                    type: 'string',
                    description:
                        'Required over MCP/API: a stable key for this start, at most 200 characters. Reuse on retries; use a new key for new work.',
                },
            },
            additionalProperties: false,
        },
        returns: '`{ runId, status, taskId, slug, origin }`.',
        callers: ['chat', 'api', 'mcp'],
        effect: 'write',
        retry: 'idempotent',
        retryNote:
            'Chat keys on its session and tool call. MCP/API requires an idempotencyKey, scoped to the authenticated caller and task; a replay returns the original run, even if inputs differ.',
        errors: [
            {
                code: 'task_not_found',
                when: 'No task in this enterprise has that id or slug.',
                handling: 'tool_error',
            },
        ],
        // External starts have a structured envelope and require runs:write; the portal route stays unchanged.
        api: { method: 'POST', path: '/v1/runs', scope: 'runs:write' },
        examples: [{ input: { slug: 'weekly-report', input: { week: '2026-W39' } } }],
        public: true,
        chatPrompt:
            'os.start_task starts a run of a task of a role you hold, an OS task, or a colleague’s task (offload). The run goes to a free holder of its role, who may not be you.',
    },
    {
        name: 'os.get_run',
        family: 'os.chat',
        summary: "A run's status, why it stopped, and its recent events.",
        // #1011: the MCP server serves this text and schema as they are, and the schema is strict: an argument it
        // does not take is refused by name before any call. The CEO chat reads the same text to pick this tool, so
        // it keeps naming `blockedReason`: "why did this run stop?" is a question this tool answers.
        description:
            "One run's status, why it stopped (blockedReason, when it is blocked), task id and input, and its last eight events. Not found for another enterprise's run.",
        inputSchema: {
            type: 'object',
            properties: { runId: { type: 'string', description: 'The run id.' } },
            required: ['runId'],
            additionalProperties: false,
        },
        returns:
            'Status, `blockedReason` (why a `blocked` run stopped, otherwise null), task id, input and the last eight events.',
        callers: ['chat', 'api', 'mcp'],
        effect: 'read',
        retry: 'safe',
        errors: [
            { code: 'runId_required', when: '`runId` is missing.', handling: 'tool_error' },
            { code: 'run_not_found', when: 'No run in this enterprise has that id.', handling: 'tool_error' },
        ],
        // A `:name` segment is filled from the argument of that name, so the placeholder is the argument's own.
        api: { method: 'GET', path: '/v1/runs/:runId/os-tool', scope: 'runs:read' },
        examples: [{ input: { runId: '5f0c1d2e-0000-4000-8000-000000000004' } }],
        public: true,
        chatPrompt: 'os.get_run reports a run.',
    },
    {
        // #1203: kept for a conversation opened to create a task. The COO interviews the person, reads the brief
        // back, and on their yes hands it to the OS's task-designing task, which writes the plan the CEO signs off.
        name: 'os.hand_off_task_brief',
        family: 'os.chat',
        summary: 'Hand the brief a person agreed to over to the OS, which designs the task from it.',
        description:
            'Hand the brief the person agreed to over to the OS, which designs the new task from it and asks the CEO to sign off on its definition before anything is created. Call it once, only after they have said yes to your read-back summary, with every part of the brief in their own words. It starts the design and returns its run; it does not create the task.',
        inputSchema: {
            type: 'object',
            properties: {
                brief: {
                    type: 'object',
                    description: 'What the person agreed to, each part in plain language.',
                    properties: {
                        goal: { type: 'string', description: 'What the job is.' },
                        success: { type: 'string', description: 'How to tell it was done well.' },
                        owner: { type: 'string', description: 'The existing team and role that should own it.' },
                        trigger: {
                            type: 'string',
                            description: 'When it runs: on demand, on a schedule said in words, or after another task.',
                        },
                        inputs: { type: 'string', description: 'What it needs each time it runs.' },
                        tools: {
                            type: 'string',
                            description: 'The connected tools it needs, naming any that is not connected.',
                        },
                        limits: {
                            type: 'string',
                            description: 'What it must never do, what needs approval, and its budget.',
                        },
                    },
                    required: ['goal', 'success', 'owner', 'trigger', 'inputs', 'tools', 'limits'],
                    additionalProperties: true,
                },
            },
            required: ['brief'],
            additionalProperties: false,
        },
        returns: '`{ runId, status, taskId }`: the run designing the task.',
        callers: ['chat', 'api'],
        purpose: 'create_task',
        effect: 'write',
        retry: 'idempotent',
        retryNote: 'Keyed on the conversation and the tool call: the same call sent twice starts one run.',
        errors: [
            {
                code: 'brief_required',
                when: '`brief` is missing, or says nothing about what the job is.',
                handling: 'tool_error',
            },
            {
                code: 'task_designer_not_found',
                when: 'The enterprise has no task-designing task to hand the brief to.',
                handling: 'tool_error',
            },
        ],
        api: { method: 'POST', path: '/v1/tasks/from-brief', scope: 'tasks:write' },
        examples: [
            {
                input: {
                    brief: {
                        goal: 'Chase unpaid invoices',
                        success: 'Every invoice over 30 days old gets one polite reminder',
                        owner: 'Finance team, accounts role',
                        trigger: 'Every Monday morning',
                        inputs: 'The list of overdue invoices',
                        tools: 'Email, which is connected',
                        limits: 'Never contact a customer twice in a week; refunds need approval; two dollars a run',
                    },
                },
            },
        ],
        public: true,
        chatPrompt:
            'os.hand_off_task_brief hands the brief the person agreed to over to the OS, which designs the task from it.',
    },
    {
        name: 'os.list_runs',
        family: 'os.chat',
        summary: "The enterprise's runs, newest first, a page at a time.",
        description:
            "List the enterprise's runs, newest first, one page at a time. Each run carries its status, task " +
            '(id, name, slug), the member it runs as, its input, and when it was created, started and finished. ' +
            'The response is { data, total, page, limit, pages }.',
        inputSchema: {
            type: 'object',
            properties: {
                status: { type: 'string', enum: RUN_STATUSES, description: 'Only runs in this status.' },
                taskId: { type: 'string', description: 'Only runs of this task.' },
                live: {
                    type: 'boolean',
                    description:
                        'true for only runs still in progress: not finished, not blocked, and not paused on spend, ' +
                        'a plan limit or a switched-off team.',
                },
                page: { type: 'integer', minimum: 1, description: 'The page to read, from 1. Defaults to 1.' },
                limit: {
                    type: 'integer',
                    minimum: 1,
                    maximum: 200,
                    description: 'Runs per page, up to 200. Defaults to 25.',
                },
            },
            additionalProperties: false,
        },
        returns:
            "`{ data, total, page, limit, pages }`, each run with its status, task, member, input and times. Over the API: the portal's rows as they are. To a run: its own enterprise's runs alone, each `{ id, status, taskId, taskName, taskSlug, memberId, assigneeName, input, blockedReason, stoppedReason, notBefore, createdAt, startedAt, finishedAt }`.",
        callers: ['run', 'api', 'mcp'],
        effect: 'read',
        retry: 'safe',
        errors: [],
        api: { method: 'GET', path: '/v1/runs', scope: 'runs:read' },
        examples: [{ input: { status: 'failed', limit: 5 } }],
        public: true,
    },
    {
        // #898: the one read that answers "what is RED?". The MCP server serves this entry as it stands (#1011),
        // over the same `GET /v1/enterprise/kpis` HQ reads, so a colour an external agent is told is the colour
        // the CEO is looking at.
        name: 'os.kpi_board',
        family: 'os.chat',
        summary: "The enterprise's KPI board: every signed-off company KPI and each team's key KPI, with its light.",
        description:
            "The enterprise's KPI board, as HQ shows it: every company KPI the owner has signed off and each " +
            "team's key KPI with its light (red off target, amber at risk, green on target), its trend (up, " +
            'down, flat), when it was last read, the cadence it is read on, and whether that reading is overdue. ' +
            'A KPI nobody has read yet has no light (`rag` is null): it is never read and overdue, not green. ' +
            'A KPI still waiting for sign-off is not on the board. A team that has set no key KPI is named with ' +
            'none rather than left out. The response is { enterprise, teams }. Read-only: recording a reading is ' +
            'not a tool here.',
        inputSchema: { type: 'object', properties: {}, additionalProperties: false },
        returns:
            "`{ enterprise, teams }`: each signed-off company KPI, and each team with its key KPI or `key: null` when it has none. Every KPI carries its light, trend, `lastReadAt`, `cadenceDays` and whether it is `overdue` — derived from the readings themselves, so it matches HQ's board KPI for KPI.",
        callers: ['api', 'mcp'],
        effect: 'read',
        retry: 'safe',
        errors: [],
        api: { method: 'GET', path: '/v1/enterprise/kpis', scope: 'enterprise:read' },
        public: true,
    },
    {
        // #1060: how a caller outside a run finds the `kpiId` that `GET /v1/kpis/:kpiId/readings` and the MCP
        // server's `kpi.list_readings` / `kpi.record_reading` take. The MCP server serves it as it stands, since its
        // catalog is generated from this registry (#1011). Not in the `os.kpi` family, whose tools the run's KPI
        // dispatcher answers.
        name: 'os.list_enterprise_kpis',
        family: 'os.chat',
        summary: 'Every KPI of the enterprise, with its owner, target, cadence, light and last reading.',
        description:
            'List every KPI of the enterprise, whatever layer owns it and whether or not it has been signed off, ' +
            'ordered by name then id. Each row carries its id (the kpiId the readings tools take), name, owner ' +
            '(layer and id), source (kind and ref), target (value, direction, AMBER band, on-target-worsening ' +
            'rule), cadence, current light, whether it is overdue, when it was signed off (null while it waits ' +
            'for sign-off, when it takes no reading), and its latest reading. The light is null when the KPI has ' +
            "no target or no reading yet. No arguments. Never another enterprise's KPIs.",
        inputSchema: { type: 'object', properties: {}, additionalProperties: false },
        returns:
            'One row per KPI, each with `id`, `name`, `ownerLayer` (`company`, `team`, `role`, `task`, or null for a company KPI set before layers), `ownerId`, `sourceKind` (`task` or `person`), `sourceRef`, `target`, `cadenceMinutes`, `light`, `overdue`, `signedOffAt` (ISO 8601, or null) and `lastReading` (`id`, `value`, `takenAt`, `recordedBy`, `runId`), or null before the first reading.',
        callers: ['api', 'mcp'],
        effect: 'read',
        retry: 'safe',
        errors: [],
        api: { method: 'GET', path: '/v1/kpis', scope: 'kpis:read' },
        examples: [{ input: {} }],
        public: true,
    },
    {
        // The Waiting list's Mark finished (#1457), for a person's own agent: `POST /v1/waiting/finish`, the route the
        // portal's row calls. Not in the `os.sense` family, whose tools only read and which a run's dispatcher answers.
        name: 'os.finish_waiting',
        family: 'os.chat',
        summary: 'Mark a Waiting row’s work finished, so it leaves the list. Touches no issue tracker.',
        description:
            'Mark one piece of work on the Waiting list finished, as its Mark finished button does. Pass pipeline ' +
            'as os.list_waiting names it. The finish is recorded on the work’s newest run, attributed to you, and ' +
            'takes it off the list; no run is started or stopped, and the issue or pull request is not touched. ' +
            'Refused when the work is not on the list, or when its execution is still doing other work. A later ' +
            'run on the same pipeline lists it again once that run ends.',
        inputSchema: {
            type: 'object',
            properties: {
                pipeline: {
                    type: 'string',
                    description: 'The work, as `owner/name#<number>`, the way os.list_waiting names it.',
                },
            },
            required: ['pipeline'],
            additionalProperties: false,
        },
        returns: '`{ finished: true }`.',
        callers: ['api', 'mcp'],
        effect: 'write',
        retry: 'idempotent',
        retryNote: 'A finished row is off the list, so a repeat is refused with not_waiting and records nothing more.',
        errors: [
            { code: 'waiting_field_required', when: '`pipeline` is missing or empty (400).', handling: 'tool_error' },
            {
                code: 'not_waiting',
                when: 'The work is not on the Waiting list, or its execution is still doing other work (409).',
                handling: 'tool_error',
            },
        ],
        api: { method: 'POST', path: '/v1/waiting/finish', scope: 'runs:write' },
        examples: [{ input: { pipeline: 'acme/rocket#935' } }],
        public: true,
    },
    {
        // The Waiting list's Restart (#1457): `POST /v1/waiting/restart`, by the paths Blockers' Retry and a route use.
        name: 'os.restart_waiting',
        family: 'os.chat',
        summary: 'Restart a Waiting row’s work at a step of its pipeline, and get the new run.',
        description:
            'Restart one piece of work on the Waiting list at a step, as its Restart button does. Pass pipeline as ' +
            'os.list_waiting names it, and task: the slug of the step to run. The same task as the newest run ' +
            'retries it; another task of its pipeline routes the work there. The new run is open, so the work ' +
            'leaves the list, and the newest run records that you restarted it and where. Refused when the work ' +
            'is not on the list.',
        inputSchema: {
            type: 'object',
            properties: {
                pipeline: {
                    type: 'string',
                    description: 'The work, as `owner/name#<number>`, the way os.list_waiting names it.',
                },
                task: {
                    type: 'string',
                    description:
                        'The slug of the task to run: the newest run’s own task to retry it, or another task of its pipeline.',
                },
            },
            required: ['pipeline', 'task'],
            additionalProperties: false,
        },
        returns:
            'The new run: its `id`, `status` (`queued`, or `pending` while its task or member is busy), `taskId` and `input`, with the rest of the run’s fields.',
        callers: ['api', 'mcp'],
        effect: 'write',
        sideEffects: 'Starts a paid run of the task named.',
        retry: 'unsafe',
        retryNote:
            'While the run it started is open the work is off the list, so a repeat is refused with not_waiting. Once that run ends, a repeat starts another.',
        errors: [
            {
                code: 'waiting_field_required',
                when: '`pipeline` or `task` is missing or empty (400).',
                handling: 'tool_error',
            },
            { code: 'not_waiting', when: 'The work is not on the Waiting list (409).', handling: 'tool_error' },
            {
                code: 'task_not_in_pipeline',
                when: 'The task is neither a successor of the newest run’s task nor one that already ran on this pipeline (409).',
                handling: 'tool_error',
            },
        ],
        api: { method: 'POST', path: '/v1/waiting/restart', scope: 'runs:write' },
        examples: [{ input: { pipeline: 'acme/rocket#935', task: 'review-pr' } }],
        public: true,
    },
    {
        // Blockers' Clear and Cancel for a person's own agent (#954), by the rule a task's `os.clear_run` follows: a
        // stale escalation is otherwise left for someone to cancel in the portal. Not `os.clear_run` itself: a run's
        // os.* tools are served at their own `api`, which is a run's credential, never a person's token.
        name: 'os.clear_blocker',
        family: 'os.chat',
        summary: 'Take a stopped run off Blockers without running it again, as its Clear or Cancel does.',
        description:
            'Take one stopped run off Blockers without running it again, because nothing more is needed: its ' +
            'issue or pull request is closed, merged or parked, or it is a stale escalation nobody has to decide. ' +
            'A failed run, or one that finished on an outcome nothing follows, is cleared; a blocked run is ' +
            'cancelled. Pass runId as os.list_blockers names it, and note: why nothing more is needed, recorded ' +
            'on the run with your name. Choose workItemState: done only when you verified the work is complete, dropped when no longer wanted, ready only when it should be picked up again, or held to keep it parked. Omitted means held; clearing alone never requeues or completes work. A later run keeps its item.',
        inputSchema: {
            type: 'object',
            properties: {
                runId: { type: 'string', description: 'The run id, as os.list_blockers names it.' },
                workItemState: {
                    type: 'string',
                    enum: ['held', 'ready', 'done', 'dropped'],
                    description:
                        'What happens to the stopped pipeline’s work item: held (default), ready to retry, done when verified complete, or dropped when no longer wanted.',
                },
                note: { type: 'string', description: 'Why nothing more is needed, in one line.' },
            },
            required: ['runId', 'note'],
            additionalProperties: false,
        },
        returns:
            'The run as it now stands: `cancelled` for a blocked run, otherwise its own status with `clearedAt` set.',
        callers: ['api', 'mcp'],
        effect: 'write',
        sideEffects:
            'Settles the stopped pipeline’s held item only as workItemState requests; omission keeps it held. Records the decision in the item history.',
        retry: 'idempotent',
        retryNote: 'A cleared or cancelled run is off Blockers, so a repeat is refused and changes nothing.',
        errors: [
            {
                code: 'invalid_work_item_state',
                when: 'workItemState is not held, ready, done or dropped (400).',
                handling: 'tool_error',
            },
            { code: 'note_required', when: '`note` is missing or empty (400).', handling: 'tool_error' },
            { code: 'run_not_found', when: 'No run with that id in this enterprise (404).', handling: 'tool_error' },
            {
                code: 'not_stopped:<status>',
                when: 'The run is not on Blockers: it is still working, or it finished on an outcome its task follows (409).',
                handling: 'tool_error',
            },
            {
                code: 'already_retried',
                when: 'A later run already retried or routed it (409). Act on that run instead.',
                handling: 'tool_error',
            },
            { code: 'already_cleared', when: 'Someone already cleared it (409).', handling: 'tool_error' },
        ],
        api: { method: 'POST', path: '/v1/runs/:runId/clear-stopped', scope: 'runs:write' },
        examples: [
            {
                input: {
                    runId: '5f0c1d2e-0000-4000-8000-000000000003',
                    note: 'Parked until its sibling closes; the escalation has nothing to decide',
                },
            },
        ],
        public: true,
    },
]

/**
 * #1735: how a teammate hands a file back in chat. Every chat is offered it, and a run never answers it. The file is
 * stored as the teammate (`member:<id>`) in the conversation it was called from, under the same rules as a file a
 * person attaches, and the reply that ends the turn carries it, so it shows under that reply as any file does.
 * Text, Markdown and CSV only: what a model writes is text, so it cannot write a PNG, a PDF or a Word file.
 *
 * Its `api` is the route a person or a token attaches a file to a conversation with: the call outside chat that
 * stores a file there, and the `chats:write` grant whoever drives the turn needs for the teammate to store one.
 */
export const OS_CHAT_FILE_TOOLS: readonly BuiltInTool[] = [
    {
        name: 'os.return_file',
        family: 'os.chat',
        summary: 'Hand back a text, Markdown or CSV file under your reply in this conversation.',
        description:
            'Hand the person a file in this conversation: a drafted document as Markdown or text, or an export as CSV. Pass the filename, ending .md, .txt or .csv, and the whole file as text; it is saved as UTF-8. The file shows under the reply that ends this turn, where anyone who may work in this conversation can download it. A person’s file limits apply: 10 MB per file and 5 files per reply, within the plan’s storage. A refused file comes back as an error saying why, and nothing is saved. The response is { id, filename, sizeBytes, type }.',
        inputSchema: {
            type: 'object',
            properties: {
                filename: {
                    type: 'string',
                    description: 'The name the file is saved under, ending .md, .txt or .csv.',
                },
                content: { type: 'string', description: 'The whole file, as text.' },
            },
            required: ['filename', 'content'],
            additionalProperties: false,
        },
        returns: '`{ id, filename, sizeBytes, type }`: the file as it shows under your reply.',
        callers: ['chat', 'api'],
        effect: 'write',
        retry: 'idempotent',
        retryNote: 'Keyed on the conversation and the tool call: the same call sent twice stores one file.',
        errors: [
            { code: 'filename_required', when: '`filename` is missing or empty.', handling: 'tool_error' },
            { code: 'content_required', when: '`content` is missing or is not text.', handling: 'tool_error' },
            {
                code: 'chat_file_unsupported_type',
                when: 'The filename does not end .txt, .md or .csv: a teammate returns text, Markdown or CSV only.',
                handling: 'tool_error',
            },
            {
                code: 'chat_file_limit_exceeded',
                when: 'The file is over 10 MB, or 5 files already wait for this reply.',
                handling: 'tool_error',
            },
            {
                code: 'plan_limit_reached',
                when: 'The file would take the enterprise past its plan’s storage.',
                handling: 'tool_error',
            },
            {
                code: 'chat_files_not_stored',
                when: 'This conversation has nowhere to store a file.',
                handling: 'tool_error',
            },
        ],
        api: { method: 'POST', path: '/v1/chats/:id/files', scope: 'chats:write' },
        examples: [{ input: { filename: 'q3-revenue.csv', content: 'Quarter,Revenue\nQ3,1200\n' } }],
        public: true,
        chatPrompt:
            'os.return_file hands the person a text, Markdown or CSV file under your reply, such as a drafted document or an export.',
    },
]

/**
 * #286: the member's step list for a request with several steps, held on the thread. Every chat is offered it and a
 * run never answers it. One tool that replaces the whole list (the epic allows exactly one): a new request replaces
 * it, an empty list clears it, and the member reads it back from the reminder at the start of each turn rather than
 * through a second tool.
 *
 * It names no `api`: no route lets a person or a token write the list, which is the member's alone (they steer it by
 * writing). Chat answers it in-process, for the conversation it was called from, and the list is read on the
 * thread's own `GET /v1/chats/:id`. Not `os.org`, so the person is never asked before a call: it changes nothing in
 * the organisation.
 */
export const OS_CHAT_STEP_TOOLS: readonly BuiltInTool[] = [
    {
        name: 'os.set_steps',
        family: 'os.chat',
        summary:
            'Record the steps you have lined up for the current request, each with its state, shown in the thread.',
        description: `Write down the steps you have lined up for the person’s current request, and where each one stands, so the person sees them in this conversation just above where they write. Send the whole list every time, in order: each call replaces the list this conversation keeps, so send every step again with its state brought up to date, and send an empty list to clear it. A state is todo, in_progress, done or skipped; mark a step skipped when the person tells you to skip it, rather than leaving it out. At most ${CHAT_STEP_LIMITS.maxSteps} steps, each title one line of at most ${CHAT_STEP_LIMITS.maxTitleChars} characters. The list belongs to this conversation alone, and you are shown it again at the start of every turn. The response is { steps }: the list as it is now kept.`,
        inputSchema: {
            type: 'object',
            properties: {
                steps: {
                    type: 'array',
                    description: `The whole list, in order: at most ${CHAT_STEP_LIMITS.maxSteps} steps. An empty list clears it.`,
                    items: {
                        type: 'object',
                        properties: {
                            title: {
                                type: 'string',
                                description: `What the step is, in a few words: at most ${CHAT_STEP_LIMITS.maxTitleChars} characters.`,
                            },
                            state: {
                                type: 'string',
                                description:
                                    'Where the step stands: todo, in_progress (under way), done, or skipped when the person said to skip it.',
                                enum: CHAT_STEP_STATES,
                            },
                        },
                        required: ['title', 'state'],
                        additionalProperties: false,
                    },
                },
            },
            required: ['steps'],
            additionalProperties: false,
        },
        returns:
            '`{ steps }`: the whole list as it is now kept, each `{ title, state }`, and an empty list once cleared.',
        callers: ['chat'],
        effect: 'write',
        retry: 'idempotent',
        retryNote:
            'Keyed on the conversation and the tool call: the same call sent twice sets the list once. Each call replaces the whole list, so a repeat leaves the same list.',
        errors: [
            { code: 'steps_required', when: '`steps` is missing or is not a list.', handling: 'tool_error' },
            {
                code: 'steps_too_many',
                when: `The list has more than ${CHAT_STEP_LIMITS.maxSteps} steps. Nothing is kept.`,
                handling: 'tool_error',
            },
            {
                code: 'step_title_required',
                when: 'A step’s title is missing, or blank once trimmed. Nothing is kept.',
                handling: 'tool_error',
            },
            {
                code: 'step_title_too_long',
                when: `A step’s title is over ${CHAT_STEP_LIMITS.maxTitleChars} characters once trimmed. Nothing is kept.`,
                handling: 'tool_error',
            },
            {
                code: 'step_state_invalid',
                when: 'A step’s state is not todo, in_progress, done or skipped. Nothing is kept.',
                handling: 'tool_error',
            },
        ],
        examples: [
            {
                input: {
                    steps: [
                        { title: 'Read the brief', state: 'done' },
                        { title: 'Draft the plan', state: 'in_progress' },
                        { title: 'Send it round for comments', state: 'todo' },
                    ],
                },
            },
        ],
        public: true,
        chatPrompt:
            'os.set_steps records the steps you have lined up for a request with several steps, each with its state, and shows them to the person in this conversation.',
    },
]
